"use client";

import React from "react";
import { Calculator, TrendingUp, ShieldCheck, ArrowRight, Info } from "lucide-react";
import { Area, AreaChart } from "recharts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ChartContainer } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const growthData = [{
  year: "Year 1",
  value: 100
}, {
  year: "Year 5",
  value: 140
}, {
  year: "Year 10",
  value: 210
}, {
  year: "Year 15",
  value: 350
}, {
  year: "Year 20",
  value: 600
}, {
  year: "Year 25",
  value: 950
}];
const chartConfig = {
  value: {
    label: "Pension Value",
    color: "var(--color-primary)"
  }
};
const benefits = [{
  icon: TrendingUp,
  title: "Lifetime Guaranteed Income",
  description: "Create a reliable income stream that you can never outlive, regardless of market conditions."
}, {
  icon: ShieldCheck,
  title: "Downside Protection",
  description: "Shield your hard-earned principal from market crashes while still participating in growth."
}, {
  icon: Calculator,
  title: "Upfront Bonuses",
  description: "Accelerate your retirement timeline with potential upfront bonuses on your initial premium."
}];
export default function TheSelfMadePension() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-self-made-pension" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 flex items-end opacity-10 pointer-events-none">
        <ChartContainer config={chartConfig} className="w-full h-[80%] min-h-[400px] -mb-10">
          <AreaChart data={growthData} margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0
        }}>
            <defs>
              <linearGradient id="fillValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="value" stroke="var(--color-primary)" strokeWidth={4} fill="url(#fillValue)" isAnimationActive={false} />
          </AreaChart>
        </ChartContainer>
      </div>

      <div ref={ref} className={`relative z-10 container mx-auto px-4 max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
            <Info className="size-4" />
            <span>Creative Finance Strategy</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight mb-6 text-balance">
            Build Your Self-Made Pension: <span className="text-primary">Guaranteed Income for Life.</span>
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-10 text-balance">
            Pensions are scarce in today's world. Take control of your retirement by converting a 401K or Roth IRA into a lifetime income source with Hybrid Annuities. Enjoy bonuses, downside protection, and peace of mind.
          </p>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="h-14 px-8 text-lg rounded-full shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-1">
                <Calculator className="mr-2 size-5" />
                Open Pension Calculator
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] dark bg-card text-card-foreground border-border">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">Estimate Your Lifetime Income</DialogTitle>
                <DialogDescription className="text-base">
                  Enter your current retirement savings details to see how a Hybrid Annuity could create your self-made pension.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-6 py-6">
                <div className="grid gap-2">
                  <label htmlFor="current-age" className="text-sm font-medium text-foreground">
                    Current Age
                  </label>
                  <input id="current-age" type="number" placeholder="e.g. 55" className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="retirement-age" className="text-sm font-medium text-foreground">
                    Target Retirement Age
                  </label>
                  <input id="retirement-age" type="number" placeholder="e.g. 65" className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="savings" className="text-sm font-medium text-foreground">
                    Current Qualified Savings (401k, IRA)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input id="savings" type="number" placeholder="250,000" className="flex h-12 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" className="w-full h-12 text-base">
                  Calculate My Estimate
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid sm:grid-cols-3 gap-8 pt-12 border-t border-border/50">
          {benefits.map((benefit, index) => <div key={index} data-index={index} className="flex flex-col items-center text-center p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:bg-card/80 transition-colors">
              <div className="size-14 rounded-full bg-primary/10 flex items-center justify-center mb-6 text-primary shadow-inner">
                <benefit.icon className="size-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">{benefit.title}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {benefit.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}